import {
    useEffect,
    useId,
    useRef,
    useState,
} from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import classes from './ImageFilePicker.module.scss';

interface ImageFilePickerProps {
    value: File[];
    onChange: (files: File[]) => void;
    onBlur: () => void;
    onPreviewChange: (open: boolean) => void;
    disabled?: boolean;
    error?: string;
}

/** Позволяет выбрать фотографии, просмотреть их и убрать из списка */
export const ImageFilePicker = (
    {
        value,
        onChange,
        onBlur,
        onPreviewChange,
        disabled,
        error,
    }: ImageFilePickerProps
) => {
    const inputId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const [previews, setPreviews] = useState<string[]>([]);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    useEffect(() => {
        const urls = value.map((file) => URL.createObjectURL(file));
        setPreviews(urls);

        return () => urls.forEach((url) => URL.revokeObjectURL(url));
    }, [value]);

    /** Открывает выбранную фотографию */
    const openPreview = (index: number) => {
        setSelectedIndex(index);
        onPreviewChange(true);
    };

    /** Закрывает подробный просмотр фотографии */
    const closePreview = () => {
        setSelectedIndex(null);
        onPreviewChange(false);
    };

    return <div className={classes.picker}>
        <label htmlFor={inputId}>Фотографии</label>
        <input
            ref={inputRef}
            id={inputId}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/bmp,image/webp"
            multiple
            hidden
            disabled={disabled}
            aria-invalid={Boolean(error)}
            aria-describedby={`${inputId}-help${error
                ? ` ${inputId}-error`
                : ''}`}
            onBlur={onBlur}
            onChange={(event) => {
                const files = Array.from(event.currentTarget.files ?? []);

                if (files.length) onChange([...value, ...files]);
                event.currentTarget.value = '';
            }}
        />
        <ActionButton
            type="button"
            label="Добавить фотографии"
            icon="pi pi-image"
            color="secondary"
            size="small"
            disabled={disabled}
            aria-describedby={`${inputId}-help${error
                ? ` ${inputId}-error`
                : ''}`}
            onClick={() => inputRef.current?.click()}
            onBlur={onBlur}
            className={classes.chooseButton}
        />
        <small id={`${inputId}-help`}>Можно выбрать несколько фотографий. Каждая — до 25 МБ</small>
        {error &&
            <Message
                id={`${inputId}-error`}
                severity="error"
                text={error}
            />
        }
        {value.length > 0 &&
            <div
                className={classes.strip}
                role="group"
                aria-label="Выбранные фотографии"
            >
                {value.map((file, index) => (
                    <div
                        key={`${file.name}-${index}`}
                        className={classes.frame}
                    >
                        <button
                            type="button"
                            className={classes.thumbnail}
                            aria-label={`Открыть фотографию ${index + 1}: ${file.name}`}
                            onClick={() => openPreview(index)}
                            disabled={disabled}
                        >
                            <img
                                src={previews[index]}
                                alt={file.name}
                            />
                        </button>
                        <ActionButton
                            type="button"
                            icon="pi pi-times"
                            iconOnly
                            size="small"
                            color="secondary"
                            aria-label={`Убрать фотографию ${index + 1}: ${file.name}`}
                            disabled={disabled}
                            onClick={() => onChange(value.filter((_, position) => position !== index))}
                            className={classes.removeButton}
                        />
                    </div>
                ))}
            </div>
        }
        <Dialog
            visible={selectedIndex !== null}
            header={`Фотография ${selectedIndex === null
                ? ''
                : selectedIndex + 1} из ${value.length}`}
            onHide={closePreview}
            className={classes.previewDialog}
            appendTo="self"
            modal
            closeOnEscape
            dismissableMask
        >
            {selectedIndex !== null &&
                <>
                    <img
                        src={previews[selectedIndex]}
                        alt={value[selectedIndex]?.name ?? 'Фотография'}
                        className={classes.previewImage}
                    />
                    <div className={classes.navigation}>
                        <Button
                            type="button"
                            icon="pi pi-chevron-left"
                            aria-label="Предыдущая фотография"
                            disabled={selectedIndex === 0}
                            onClick={() => setSelectedIndex(selectedIndex - 1)}
                        />
                        <Button
                            type="button"
                            icon="pi pi-chevron-right"
                            aria-label="Следующая фотография"
                            disabled={selectedIndex === value.length - 1}
                            onClick={() => setSelectedIndex(selectedIndex + 1)}
                        />
                    </div>
                </>
            }
        </Dialog>
    </div>;
};
